import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Activity, Building2, Cpu, Database, AlertTriangle, Heart,
    ArrowRight, CheckCircle2, XCircle, Server, Radio, WifiOff,
} from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { useWS } from "@/context/WSContext";
import {
    SACard, MetricCard, HealthRing, SeverityDot, StatusPill, Sparkline,
    CountUp, formatBytes, timeAgo,
} from "@/components/superadmin/SAPrimitives";

export default function SuperAdminOverview() {
    const [overview, setOverview] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [activity, setActivity] = useState([]);
    const [timeseries, setTimeseries] = useState([]);
    const [topTenants, setTopTenants] = useState([]);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);
    const [liveTick, setLiveTick] = useState(null);
    const { subscribe, connected } = useWS();

    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const [ov, ts, al, act, tsj, tt] = await Promise.all([
                    http.get("/superadmin/overview"),
                    http.get("/superadmin/tenants?sort=last_activity&order=desc&limit=8"),
                    http.get("/superadmin/alerts"),
                    http.get("/superadmin/activity?limit=12"),
                    http.get("/superadmin/analytics/jobs-timeseries?days=30"),
                    http.get("/superadmin/analytics/top-tenants?days=30&limit=6"),
                ]);
                if (!alive) return;
                setOverview(ov.data);
                setTenants(ts.data.tenants || []);
                setAlerts(al.data.alerts || []);
                setActivity(act.data.events || []);
                setTimeseries(tsj.data.series || []);
                setTopTenants(tt.data.tenants || []);
            } catch (e) {
                if (alive) setErr(errMsg(e));
            } finally {
                if (alive) setLoading(false);
            }
        };
        load();
        // Fallback 60s poll catches dropped WS frames. When WS is healthy
        // this barely fires; when it's dead the UI still refreshes.
        const iv = setInterval(load, 60_000);
        return () => { alive = false; clearInterval(iv); };
    }, []);

    // Live WS patching: `sa.overview.update` supersedes the polled snapshot
    // without touching the other panels (alerts/activity stay on their own
    // cadence — they don't animate and aren't worth the extra socket weight).
    useEffect(() => {
        const unsub = subscribe((msg) => {
            if (msg?.event === "sa.overview.update" && msg?.data) {
                setOverview(msg.data);
                setLiveTick(new Date());
            }
        });
        return unsub;
    }, [subscribe]);

    if (err && !overview) {
        return (
            <SACard data-testid="sa-overview-error" className="text-red-300">
                Failed to load overview: {err}
            </SACard>
        );
    }

    const o = overview || {};
    const bytesSeries = timeseries.map((b) => b.bytes);
    const completedSeries = timeseries.map((b) => b.completed);
    const failedSeries = timeseries.map((b) => b.failed);

    return (
        <div className="space-y-5" data-testid="sa-overview">
            {/* Header */}
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-[24px] font-semibold tracking-tight">Platform Overview</h1>
                    <div className="mt-1 flex items-center gap-2 text-[12px] text-white/55">
                        <span>Global metrics across every tenant</span>
                        <span className="text-white/30">·</span>
                        {connected ? (
                            <span className="flex items-center gap-1 text-emerald-300" data-testid="sa-live-indicator">
                                <Radio size={10} className="animate-pulse" />
                                Live
                                {liveTick && (
                                    <span className="text-white/45">
                                        · last tick {liveTick.toLocaleTimeString()}
                                    </span>
                                )}
                            </span>
                        ) : (
                            <span className="flex items-center gap-1 text-amber-300">
                                <WifiOff size={10} /> Reconnecting · 60 s fallback poll
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Top metrics row */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="sa-metrics-row">
                <MetricCard
                    icon={Building2}
                    label="Total Tenants"
                    value={o.total_tenants || 0}
                    sublabel={`${o.active_tenants || 0} active · ${o.suspended_tenants || 0} suspended`}
                    testId="sa-metric-tenants"
                />
                <MetricCard
                    icon={Cpu}
                    label="Total Agents"
                    value={o.total_agents || 0}
                    sublabel={`${o.online_agents || 0} online (${o.agent_online_rate || 0}%)`}
                    tone={(o.agent_online_rate || 0) >= 90 ? "good" : "warn"}
                    testId="sa-metric-agents"
                />
                <MetricCard
                    icon={Activity}
                    label="Active Jobs"
                    value={o.active_jobs || 0}
                    sublabel="Running + queued"
                    testId="sa-metric-active-jobs"
                />
                <MetricCard
                    icon={Database}
                    label="Data Transferred (30d)"
                    value={formatBytes(o.bytes_transferred_30d || 0)}
                    sublabel={`${o.completed_jobs_30d || 0} completed jobs`}
                    testId="sa-metric-bytes"
                />
                <MetricCard
                    icon={AlertTriangle}
                    label="Errors (24h)"
                    value={o.errors_24h || 0}
                    sublabel={`${o.failed_jobs_30d || 0} failed jobs 30d`}
                    tone={(o.errors_24h || 0) > 10 ? "bad" : (o.errors_24h || 0) > 0 ? "warn" : "good"}
                    testId="sa-metric-errors"
                />
                <MetricCard
                    icon={Heart}
                    label="Health Score"
                    value={`${o.health_score || 0}`}
                    sublabel="Jobs × agents, weighted"
                    tone={(o.health_score || 0) >= 85 ? "good" : (o.health_score || 0) >= 60 ? "warn" : "bad"}
                    testId="sa-metric-health"
                />
            </div>

            {/* Health · Activity · Alerts row */}
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
                {/* Platform Health */}
                <SACard className="lg:col-span-4" data-testid="sa-health-card">
                    <div className="mb-3 flex items-center justify-between">
                        <div className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
                            Platform Health
                        </div>
                        <Heart size={12} className="text-[#00C2FF]" />
                    </div>
                    <div className="flex flex-col items-center py-2">
                        <HealthRing
                            score={o.health_score || 0}
                            label={
                                (o.health_score || 0) >= 85
                                    ? "Healthy"
                                    : (o.health_score || 0) >= 60
                                        ? "Degraded"
                                        : "At risk"
                            }
                        />
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3 border-t border-white/5 pt-4 text-[12px]">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-white/45">
                                Agent Online Rate
                            </div>
                            <div className="mt-0.5 font-semibold">{o.agent_online_rate || 0}%</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-white/45">
                                Job Success Rate
                            </div>
                            <div className="mt-0.5 font-semibold">{o.job_success_rate || 0}%</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-white/45">
                                Active Jobs
                            </div>
                            <div className="mt-0.5 font-semibold">{o.active_jobs || 0}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-white/45">
                                Errors 24h
                            </div>
                            <div className="mt-0.5 font-semibold">{o.errors_24h || 0}</div>
                        </div>
                    </div>
                </SACard>

                {/* Live Activity */}
                <SACard className="lg:col-span-4" data-testid="sa-activity-card">
                    <div className="mb-3 flex items-center justify-between">
                        <div className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
                            Live Activity
                        </div>
                        <Activity size={12} className="text-[#00C2FF]" />
                    </div>
                    <ul className="space-y-2 overflow-hidden">
                        {activity.length === 0 && (
                            <li className="py-6 text-center text-[12px] text-white/40">
                                No activity in the last 24 h.
                            </li>
                        )}
                        {activity.slice(0, 8).map((ev, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-2 border-b border-white/5 pb-2 text-[12px] last:border-none"
                            >
                                {ev.kind === "job" ? (
                                    ev.status === "completed" ? (
                                        <CheckCircle2 size={12} className="mt-0.5 shrink-0 text-emerald-300" />
                                    ) : ev.status === "failed" || ev.status === "error" ? (
                                        <XCircle size={12} className="mt-0.5 shrink-0 text-red-300" />
                                    ) : (
                                        <Activity size={12} className="mt-0.5 shrink-0 text-[#00C2FF]" />
                                    )
                                ) : (
                                    <Server size={12} className="mt-0.5 shrink-0 text-white/50" />
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-white/85">{ev.message}</div>
                                    <div className="mt-0.5 text-[10.5px] text-white/45">
                                        {ev.tenant_name || "—"} · {timeAgo(ev.timestamp)}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </SACard>

                {/* Top Alerts */}
                <SACard className="lg:col-span-4" data-testid="sa-alerts-card">
                    <div className="mb-3 flex items-center justify-between">
                        <div className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
                            Top Alerts
                        </div>
                        <Link
                            to="/superadmin/alerts"
                            className="text-[11px] text-[#00C2FF] hover:underline"
                        >
                            View all →
                        </Link>
                    </div>
                    <ul className="space-y-2">
                        {alerts.length === 0 && (
                            <li className="py-6 text-center text-[12px] text-white/40">
                                All clear — no platform alerts.
                            </li>
                        )}
                        {alerts.slice(0, 5).map((a, i) => (
                            <li
                                key={i}
                                className="rounded-md border p-2.5 text-[12px]"
                                style={{
                                    borderColor:
                                        a.severity === "critical"
                                            ? "rgba(248,113,113,0.30)"
                                            : a.severity === "warning"
                                                ? "rgba(251,191,36,0.30)"
                                                : "rgba(56,189,248,0.30)",
                                    background:
                                        a.severity === "critical"
                                            ? "rgba(248,113,113,0.08)"
                                            : a.severity === "warning"
                                                ? "rgba(251,191,36,0.06)"
                                                : "rgba(56,189,248,0.06)",
                                }}
                            >
                                <div className="flex items-center gap-2">
                                    <SeverityDot severity={a.severity} />
                                    <span className="text-[10px] uppercase tracking-wider text-white/55">
                                        {a.severity}
                                    </span>
                                </div>
                                <div className="mt-1 text-white/90">{a.message}</div>
                                <div className="mt-1 text-[10.5px] text-white/45">
                                    {a.tenant_name || "—"}
                                </div>
                            </li>
                        ))}
                    </ul>
                </SACard>
            </div>

            {/* Analytics row */}
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
                <SACard className="lg:col-span-5" data-testid="sa-bytes-chart">
                    <div className="mb-2 flex items-center justify-between">
                        <div>
                            <div className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
                                Data Transfer · last 30d
                            </div>
                            <div className="mt-1 text-[20px] font-semibold">
                                {formatBytes(o.bytes_transferred_30d || 0)}
                            </div>
                        </div>
                    </div>
                    <Sparkline values={bytesSeries} color="#00C2FF" height={88} />
                </SACard>

                <SACard className="lg:col-span-4" data-testid="sa-jobs-chart">
                    <div className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
                        Jobs · last 30d
                    </div>
                    <div className="mb-1 flex items-center gap-4 text-[11px]">
                        <span className="flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full bg-emerald-400" /> Completed
                        </span>
                        <span className="flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full bg-red-400" /> Failed
                        </span>
                    </div>
                    <div className="relative h-[88px]">
                        <div className="absolute inset-0">
                            <Sparkline values={completedSeries} color="#34d399" height={88} fill={false} />
                        </div>
                        <div className="absolute inset-0">
                            <Sparkline values={failedSeries} color="#f87171" height={88} fill={false} />
                        </div>
                    </div>
                </SACard>

                <SACard className="lg:col-span-3" data-testid="sa-top-tenants">
                    <div className="mb-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
                        Top Tenants · by data
                    </div>
                    <ul className="space-y-2">
                        {topTenants.length === 0 && (
                            <li className="text-[12px] text-white/40">No transfers in range.</li>
                        )}
                        {topTenants.map((t, i) => {
                            const max = topTenants[0]?.bytes || 1;
                            const pct = (t.bytes / max) * 100;
                            return (
                                <li key={t.tenant_id} className="text-[12px]">
                                    <div className="mb-1 flex justify-between">
                                        <Link
                                            to={`/superadmin/tenants/${t.tenant_id}`}
                                            className="truncate text-white/85 hover:text-[#00C2FF]"
                                        >
                                            {i + 1}. {t.tenant_name || t.tenant_slug || "unnamed"}
                                        </Link>
                                        <span className="ml-2 shrink-0 text-white/55">
                                            {formatBytes(t.bytes)}
                                        </span>
                                    </div>
                                    <div className="h-1 rounded-full bg-white/5">
                                        <div
                                            className="h-full rounded-full"
                                            style={{
                                                width: `${pct}%`,
                                                background:
                                                    "linear-gradient(90deg, #004AAD, #00C2FF)",
                                            }}
                                        />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </SACard>
            </div>

            {/* Recent tenants table */}
            <SACard className="p-0" data-testid="sa-recent-tenants">
                <div className="flex items-center justify-between px-5 py-4">
                    <div className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
                        Recent Tenants
                    </div>
                    <Link
                        to="/superadmin/tenants"
                        className="flex items-center gap-1 text-[11px] text-[#00C2FF] hover:underline"
                    >
                        View all <ArrowRight size={10} />
                    </Link>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="border-y border-white/5 text-left text-[10px] uppercase tracking-wider text-white/45">
                                <th className="px-5 py-2.5 font-medium">Company</th>
                                <th className="px-2 py-2.5 font-medium">Plan</th>
                                <th className="px-2 py-2.5 font-medium">Status</th>
                                <th className="px-2 py-2.5 text-right font-medium">Users</th>
                                <th className="px-2 py-2.5 text-right font-medium">Agents</th>
                                <th className="px-2 py-2.5 text-right font-medium">Data (30d)</th>
                                <th className="px-2 py-2.5 text-right font-medium">Jobs</th>
                                <th className="px-2 py-2.5 font-medium">Last Activity</th>
                                <th className="px-5 py-2.5" />
                            </tr>
                        </thead>
                        <tbody>
                            {tenants.map((t) => (
                                <tr
                                    key={t.id}
                                    className="border-b border-white/5 transition-colors hover:bg-white/[0.03]"
                                    data-testid={`sa-tenant-row-${t.slug || t.id}`}
                                >
                                    <td className="px-5 py-3">
                                        <Link
                                            to={`/superadmin/tenants/${t.id}`}
                                            className="font-semibold text-white hover:text-[#00C2FF]"
                                        >
                                            {t.name}
                                        </Link>
                                        <div className="text-[10.5px] text-white/45">{t.slug || "—"}</div>
                                    </td>
                                    <td className="px-2 py-3 uppercase text-white/70">
                                        {t.plan || "free"}
                                    </td>
                                    <td className="px-2 py-3">
                                        <StatusPill
                                            status={t.deleted_at ? (t.status === "purged" ? "purged" : "deleted") : t.status || "active"}
                                        />
                                    </td>
                                    <td className="px-2 py-3 text-right text-white/80">
                                        {t.users_count}
                                    </td>
                                    <td className="px-2 py-3 text-right text-white/80">
                                        {t.online_agents_count}/{t.agents_count}
                                    </td>
                                    <td className="px-2 py-3 text-right text-white/80">
                                        {formatBytes(t.bytes_transferred_30d)}
                                    </td>
                                    <td className="px-2 py-3 text-right text-white/80">
                                        {t.active_jobs}
                                    </td>
                                    <td className="px-2 py-3 text-white/65">
                                        {timeAgo(t.last_activity)}
                                    </td>
                                    <td className="px-5 py-3 text-right">
                                        <Link
                                            to={`/superadmin/tenants/${t.id}`}
                                            className="text-[11px] text-[#00C2FF] hover:underline"
                                        >
                                            View →
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                            {!loading && tenants.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="px-5 py-8 text-center text-white/40">
                                        No tenants yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </SACard>
        </div>
    );
}
